import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RamalhoLogo } from "@/components/RamalhoLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, LogOut, ArrowUpRight, ExternalLink, Users, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminDashboard() {
  const { logout, username } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({
    name: "",
    installation_number: "",
    address: "",
    plant_capacity_kwp: "",
    notes: "",
  });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) {
      toast.error("Falha ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        installation_number: form.installation_number || null,
        address: form.address || null,
        plant_capacity_kwp: form.plant_capacity_kwp ? parseFloat(form.plant_capacity_kwp) : null,
        notes: form.notes || null,
      };
      const { data } = await api.post("/clients", payload);
      toast.success(`Cliente "${data.name}" criado`);
      setOpenCreate(false);
      setForm({ name: "", installation_number: "", address: "", plant_capacity_kwp: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar cliente");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente excluído");
      load();
    } catch (e) {
      toast.error("Falha ao excluir");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E5E5DF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <RamalhoLogo />
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-[#4A4A4A]">
              Olá, <span className="font-medium text-[#1A1A1A]">{username}</span>
            </span>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={handleLogout}
              className="rounded-full h-9 text-sm border-[#E5E5DF]"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Section header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <span className="overline text-[#52796F]">Dashboard administrativo</span>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-[#1A1A1A] mt-2">
              Clientes
            </h1>
            <p className="text-sm text-[#4A4A4A] mt-2 max-w-lg">
              Cadastre clientes, envie logomarcas, faça upload das contas EDP com extração automática
              de dados e registre a geração da usina.
            </p>
          </div>
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-client-btn"
                className="rounded-full bg-[#1B4332] text-white hover:bg-[#122d22] h-11 px-5"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Novo cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Novo cliente</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4" data-testid="new-client-form">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                    Nome *
                  </Label>
                  <Input
                    id="c-name"
                    data-testid="new-client-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Padaria do João"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="c-inst" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                      Unid. consumidora
                    </Label>
                    <Input
                      id="c-inst"
                      data-testid="new-client-uc"
                      value={form.installation_number}
                      onChange={(e) => setForm({ ...form, installation_number: e.target.value })}
                      placeholder="123456789"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="c-cap" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                      Capacidade (kWp)
                    </Label>
                    <Input
                      id="c-cap"
                      data-testid="new-client-capacity"
                      type="number"
                      step="0.01"
                      value={form.plant_capacity_kwp}
                      onChange={(e) => setForm({ ...form, plant_capacity_kwp: e.target.value })}
                      placeholder="10.5"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-addr" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                    Endereço
                  </Label>
                  <Input
                    id="c-addr"
                    data-testid="new-client-address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Rua ..., Cidade - UF"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-notes" className="text-xs uppercase tracking-[0.2em] text-[#52796F]">
                    Observações
                  </Label>
                  <Textarea
                    id="c-notes"
                    data-testid="new-client-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Detalhes internos..."
                    rows={3}
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={creating}
                    data-testid="new-client-submit"
                    className="rounded-full bg-[#1B4332] text-white hover:bg-[#122d22]"
                  >
                    {creating ? "Salvando..." : "Criar cliente"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Empty state */}
        {!loading && clients.length === 0 && (
          <div
            data-testid="clients-empty"
            className="rounded-lg border border-dashed border-[#E5E5DF] bg-white p-16 text-center"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-[#D8F3DC] flex items-center justify-center mb-4">
              <Users className="h-5 w-5 text-[#1B4332]" />
            </div>
            <h3 className="font-display text-xl font-semibold text-[#1A1A1A]">Nenhum cliente ainda</h3>
            <p className="text-sm text-[#4A4A4A] mt-2 max-w-sm mx-auto">
              Crie o primeiro cliente para começar a gerenciar contas e geração da usina.
            </p>
            <Button
              onClick={() => setOpenCreate(true)}
              className="mt-6 rounded-full bg-[#1B4332] text-white hover:bg-[#122d22]"
              data-testid="empty-new-client-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Criar primeiro cliente
            </Button>
          </div>
        )}

        {loading && (
          <div className="text-sm text-[#4A4A4A]">Carregando...</div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((c) => (
            <div
              key={c.id}
              data-testid={`client-card-${c.slug}`}
              className="group rounded-lg border border-[#E5E5DF] bg-white p-6 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(0,0,0,0.04)] transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="overline text-[#52796F]">Cliente</span>
                  <h3 className="font-display text-xl font-semibold text-[#1A1A1A] mt-1.5 leading-tight">
                    {c.name}
                  </h3>
                </div>
                {c.plant_capacity_kwp != null && (
                  <span className="mono-num text-[11px] tracking-tight rounded-full bg-[#D8F3DC] text-[#1B4332] px-2.5 py-1">
                    {c.plant_capacity_kwp} kWp
                  </span>
                )}
              </div>
              <div className="space-y-1.5 text-xs text-[#4A4A4A]">
                {c.installation_number && (
                  <div>
                    <span className="overline">UC</span>{" "}
                    <span className="mono-num ml-1 text-[#1A1A1A]">{c.installation_number}</span>
                  </div>
                )}
                {c.address && <div className="line-clamp-1">{c.address}</div>}
              </div>
              <div className="mt-6 flex items-center justify-between gap-2">
                <Link
                  to={`/admin/clientes/${c.id}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#1B4332] hover:underline"
                  data-testid={`open-client-${c.slug}`}
                >
                  Gerenciar <ArrowUpRight className="h-4 w-4" />
                </Link>
                <div className="flex items-center gap-2">
                  <a
                    href={`/cliente/${c.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#52796F] hover:text-[#1B4332] inline-flex items-center gap-1"
                    data-testid={`view-public-${c.slug}`}
                  >
                    Página pública <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`delete-client-${c.slug}`}
                        className="p-1.5 rounded-md text-[#D90429] hover:bg-[#D90429]/10 transition-colors"
                        aria-label="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Todas as contas e registros de geração deste cliente serão excluídos. Essa ação não
                          pode ser desfeita.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(c.id)}
                          className="bg-[#D90429] hover:bg-[#a10321]"
                        >
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
